import { loincCoding } from '#src/models/loinc.ts'
import {
  MOCA_DOMAINS,
  MOCA_EDUCATION_ANSWERS,
} from '#src/terminology/instruments/instrumentTerminology.ts'
import {
  numberItem,
  ownCoding,
  scoreBand,
  scoredItem,
  totalScore,
} from '#src/terminology/instruments/templateParts.ts'
import type {
  InstrumentSection,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'

const MOCA_MAX = 30

const SECTIONS: InstrumentSection[] = [
  {
    id: 'domains',
    items: MOCA_DOMAINS.map(([domain, label, max, hint]) =>
      numberItem(
        ownCoding(`moca-${domain}`, `MoCA ${label.toLowerCase()}`),
        { min: 0, max },
        { label, hint }
      )
    ),
  },
  {
    id: 'adjustment',
    items: [
      scoredItem(
        ownCoding(
          'moca-education',
          'MoCA 12 years or fewer of formal education'
        ),
        MOCA_EDUCATION_ANSWERS,
        {
          scaleType: 'Nom',
          label: '12 years or fewer of formal education (adds 1 point)',
          required: false,
        }
      ),
      numberItem(
        ownCoding('moca-memory-index-score', 'MoCA memory index score'),
        { min: 0, max: 15 },
        {
          label: 'Memory index score (MIS)',
          hint: 'Per word: recalled uncued 3, with a category cue 2, from multiple choice 1',
          required: false,
          scored: false,
        }
      ),
    ],
  },
]

const DOMAIN_CODINGS = SECTIONS[0].items.map((item) => item.coding)

// The seven domains plus one point for 12 years or fewer of education,
// capped at 30. A domain left blank gives no total; the education answer may
// be blank, which adds nothing.
export const MOCA_TEMPLATE: InstrumentTemplate = {
  id: 'moca',
  version: '1',
  panel: loincCoding('72133-2', 'Montreal Cognitive Assessment [MoCA]'),
  title: 'Montreal Cognitive Assessment (MoCA)',
  copyright: 'MoCA© Z. Nasreddine MD. Reproduced with permission.',
  reference:
    'https://geriatrictoolkit.missouri.edu/cog/MoCA-8.3-English-Test-2018-04.pdf',
  sections: SECTIONS,
  total: totalScore({
    templateId: 'moca',
    coding: loincCoding('72172-0', 'Total score [MoCA]'),
    sections: SECTIONS,
    completeness: { allowMissing: 1 },
    rule: (sum) => ({
      ...sum,
      compute: (inputs) => {
        const result = DOMAIN_CODINGS.some(
          (coding) => inputs.score(coding) === null
        )
          ? null
          : sum.compute(inputs)
        return result?.kind === 'number'
          ? { ...result, value: Math.min(MOCA_MAX, result.value) }
          : result
      },
    }),
    // Nasreddine ZS et al. J Am Geriatr Soc 2005;53(4):695-9,
    // doi:10.1111/j.1532-5415.2005.53221.x.
    bands: [
      scoreBand({ min: 26 }, 'moca-normal', 'Normal', 'green'),
      scoreBand({ max: 25 }, 'moca-below-normal', 'Below normal', 'red'),
    ],
  }),
}
